/** The 3-char case-safety suffix of a 15-char Salesforce id (the step `convertTo18CharId` appends). */
export function sfdcCaseSuffix(body15: string): string {
  const suffixChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ012345';
  let suffix = '';

  // Process 3 chunks of 5 characters each
  for (let chunk = 0; chunk < 3; chunk++) {
    let flags = 0;
    for (let pos = 0; pos < 5; pos++) {
      const char = body15[chunk * 5 + pos];
      // Check if character is uppercase A-Z
      if (char >= 'A' && char <= 'Z') {
        flags |= 1 << pos;
      }
    }
    suffix += suffixChars[flags];
  }

  return suffix;
}

/**
 * Convert a 15-character Salesforce ID to 18-character format.
 * Salesforce IDs are case-sensitive in 15-char format, but the 18-char format
 * adds a 3-character suffix that makes them case-insensitive.
 *
 * The algorithm:
 * 1. Split the 15-char ID into three 5-char chunks
 * 2. For each chunk, create a 5-bit number where bit N is 1 if char N is uppercase A-Z
 * 3. Map each 5-bit number to a character: 0-25 → A-Z, 26-31 → 0-5
 * 4. Append the 3 characters to the original ID
 *
 * Lives in `src/shared/flow/` (pure, no Prisma/Middleman imports) so it is
 * importable both from backend services (`src/services/salesforce-resolution`,
 * which re-exports it for its existing call sites) and from the frontend's
 * short entity-route resolver (`web/app-home/src/components/flow/entity-routes.tsx`),
 * which must normalize a pasted 15-char id to 18-char BEFORE building any
 * redirect URL (plan D2).
 */
export function convertTo18CharId(objectId: string): string {
  const trimmed = objectId.trim();
  if (trimmed.length !== 15) {
    return trimmed;
  }

  return trimmed + sfdcCaseSuffix(trimmed);
}
